/** Who you are signed in as, and the way out. Present in the rail on every console page. */

import { useNavigate } from "@tanstack/react-router";

import { Popover } from "@/components/ui";
import { isSignedOut } from "@/lib/api";

import { useAccount, useSignOut } from "../account/lib/queries";

const LINKS = [
  { label: "Account", to: "/console/account" },
  { label: "Plan", to: "/console/plan" },
] as const;

export function AccountMenu() {
  const navigate = useNavigate();
  const account = useAccount();
  const signOut = useSignOut();

  const handle = account.data?.handle ?? "";
  const name = account.data?.display_name ?? handle;
  const signedOut = isSignedOut(account.error);

  /**
   * A signed-out visitor used to read "Signed in".
   *
   * The name falls back whenever there is no account, and a 401 is a case with no account, so the
   * console showed a signed-in chip above a page whose every panel said "this request is not
   * signed in". Being told to sign in is the whole of what that person needs.
   */
  if (signedOut) {
    return (
      <button
        type="button"
        className="cx-account-trigger"
        onClick={() => void navigate({ to: "/sign-in" })}
      >
        <span className="cx-account-avatar" aria-hidden="true">
          ?
        </span>
        <span className="cx-account-name">Sign in</span>
      </button>
    );
  }

  return (
    <Popover
      side="top"
      menuLabel="Account"
      minWidth={240}
      triggerClassName="cx-account-trigger"
      label={
        <>
          <span className="cx-account-avatar" aria-hidden="true">
            {(name.slice(0, 1) || "?").toUpperCase()}
          </span>
          <span className="cx-account-name">
            {name === "" ? "Signed in" : name}
          </span>
        </>
      }
    >
      {(close) => (
        <>
          {handle === "" ? null : (
            <div className="al-popover-label">{handle}</div>
          )}
          {LINKS.map((link) => (
            <button
              key={link.to}
              type="button"
              role="menuitem"
              className="al-popover-item"
              onClick={() => {
                close();
                void navigate({ to: link.to });
              }}
            >
              {link.label}
            </button>
          ))}
          <div className="al-popover-sep" />
          <button
            type="button"
            role="menuitem"
            className="al-popover-item al-popover-item-danger"
            disabled={signOut.isPending}
            onClick={() => {
              close();
              signOut.mutate(undefined, {
                onSuccess: () => {
                  window.location.assign("/");
                },
              });
            }}
          >
            Sign out
          </button>
        </>
      )}
    </Popover>
  );
}
